// Pure balance-calculation logic for the Expense Tracking MVP
// (docs/competitive-analysis.md's "Expense tracking MVP scope"). No React/
// Redux/DOM dependency, kept identical across both repos (same convention
// as Guide.js itself) so neither platform's UI layer duplicates this math
// inline — both ExpensesSection.js components import and call this.
//
// "Plain arithmetic, no settlement algorithm" per the decided scope: this
// only computes what each person paid vs. what they owe, never who should
// pay whom to settle up.

// Groups expenses by currency first, rather than summing amounts in
// different currencies into one number — multi-currency conversion is
// explicitly out of scope, but silently collapsing $ and € together would
// be a real correctness bug, not just a missing nice-to-have. Each group
// gets its own balance table.
export function computeBalancesByCurrency(expenses, tripMembers) {
  const byCurrency = {};
  (expenses || []).forEach((expense) => {
    const currency = expense.currency || "";
    if (!byCurrency[currency]) byCurrency[currency] = [];
    byCurrency[currency].push(expense);
  });
  const result = {};
  Object.keys(byCurrency).forEach((currency) => {
    result[currency] = computeBalances(byCurrency[currency], tripMembers);
  });
  return result;
}

// computeBalances(expenses, tripMembers) -> [{memberId, name, paid, owed, net}]
// Assumes all expenses passed in share one currency — see
// computeBalancesByCurrency above for the multi-currency entry point most
// callers actually want.
export function computeBalances(expenses, tripMembers) {
  const paid = {};
  const owed = {};
  (tripMembers || []).forEach((member) => {
    paid[member.id] = 0;
    owed[member.id] = 0;
  });
  (expenses || []).forEach((expense) => {
    if (expense.paidBy != null && paid[expense.paidBy] !== undefined) {
      paid[expense.paidBy] += expense.amount;
    }
    (expense.splits || []).forEach((split) => {
      if (owed[split.memberId] === undefined) return;
      owed[split.memberId] += expense.amount * ((split.percent || 0) / 100);
    });
  });
  return (tripMembers || []).map((member) => ({
    memberId: member.id,
    name: member.name,
    paid: paid[member.id] || 0,
    owed: owed[member.id] || 0,
    net: (paid[member.id] || 0) - (owed[member.id] || 0),
  }));
}

// Guard for removing a trip member — true if they're referenced anywhere
// in expenses (as payer or in a split), meaning the UI should block the
// removal ("remove them from expenses first") rather than leave an
// orphaned id with no balance row.
export function isMemberReferencedInExpenses(memberId, expenses) {
  return (expenses || []).some(
    (expense) =>
      expense.paidBy === memberId || (expense.splits || []).some((split) => split.memberId === memberId)
  );
}
